/**
 * Crescent Flexipack — multi-step Request-for-Quote.
 * Four steps: Contact → Packaging → Specs → Artwork & notes.
 * File upload goes to /api/crescent/rfq/upload (base64, max 15MB).
 * Final submit → /api/crescent/rfq/submit, returns rfq_id + lead_score.
 */
import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Loader2, CheckCircle2, UploadCloud, Paperclip } from "lucide-react";
import CrescentLayout from "./CrescentLayout";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "contact",   label: "Contact" },
  { key: "packaging", label: "Packaging" },
  { key: "specs",     label: "Specs" },
  { key: "artwork",   label: "Artwork & notes" },
];

const PRODUCT_CATEGORIES = [
  "Food & Beverages", "Seafood & Frozen Food", "Pet Food", "Tea / Coffee / Spices",
  "Agriculture", "Personal Care", "Supermarket Private Label", "Industrial", "Other",
];

const PACKAGING_FORMATS = [
  "Stand-up pouch (bottom gusset)",
  "Stand-up pouch with zipper",
  "Stand-up pouch with spout",
  "Center-seal pouch",
  "Three-side-seal pouch",
  "Gusseted pouch",
  "Printed laminated roll (for FFS)",
  "LDPE / LLDPE film or sheet",
  "Help me choose",
];

export default function CrescentRequestQuote() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  usePageSeo({
    title: "Request a Quote — Crescent Flexi Pack",
    description: "Submit a request for custom flexible packaging — stand-up pouches, printed rolls, laminated structures. Our team confirms specs in writing before production.",
  });
  useBreadcrumbSchema([
    { name: "Home", url: "/crescent" },
    { name: "Request a Quote", url: "/crescent/request-quote" },
  ]);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target?.value ?? e }));

  const canNext = () => {
    if (step === 0) return form.name && form.email && form.country && form.product_category;
    if (step === 1) return !!form.packaging_format;
    return true;
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File must be under 15MB"); return; }
    setUploading(true);
    try {
      const data = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => reject(r.error);
        r.readAsDataURL(file);
      });
      const resp = await axios.post(`${API}/crescent/rfq/upload`, { filename: file.name, data });
      setUploads((u) => [...u, resp.data]);
      toast.success(`Uploaded ${file.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, upload_ids: uploads.map((u) => u.upload_id) };
      const { data } = await axios.post(`${API}/crescent/rfq/submit`, payload);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  if (result) {
    return (
      <CrescentLayout>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="rfq-success">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
            <CheckCircle2 size={28} />
          </div>
          <h1 className="mt-4 font-heading font-extrabold text-slate-900 text-3xl">Thank you — RFQ received.</h1>
          <p className="mt-2 text-slate-600">Our Crescent team will review your request and come back with specifications and quote in writing.</p>
          <p className="mt-6 text-xs font-mono text-slate-400">Reference: {result.rfq_id}</p>
          <Link to="/crescent" className="mt-8 inline-flex items-center gap-2 bg-emerald-600 text-white font-bold px-5 py-2.5 rounded-full text-sm">
            Back to Crescent <ArrowRight size={14} />
          </Link>
        </div>
      </CrescentLayout>
    );
  }

  return (
    <CrescentLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="crescent-rfq-page">
        <nav className="text-xs text-emerald-700 mb-3">
          <Link to="/crescent" className="hover:underline">Home</Link>
          <span className="mx-2">›</span>
          <span>Request a Quote</span>
        </nav>
        <h1 className="font-heading font-extrabold text-slate-900 text-4xl">Request a Packaging Quote</h1>
        <p className="mt-2 text-slate-600">Four short steps. The more detail you share, the more precise our team's response.</p>

        {/* Stepper */}
        <ol className="mt-8 flex items-center gap-2" data-testid="rfq-stepper">
          {STEPS.map((s, i) => (
            <li key={s.key} className={`flex-1 flex items-center gap-2 ${i < step ? "text-emerald-600" : i === step ? "text-slate-900" : "text-slate-300"}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i < step ? "bg-emerald-600 text-white" : i === step ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-400"}`}>
                {i < step ? "✓" : i + 1}
              </span>
              <span className="text-xs font-semibold hidden sm:inline">{s.label}</span>
              {i < STEPS.length - 1 && <span className="flex-1 h-px bg-slate-200" />}
            </li>
          ))}
        </ol>

        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 lg:p-8">
          {step === 0 && (
            <div className="space-y-4" data-testid="rfq-step-contact">
              <h2 className="font-heading font-bold text-xl">Contact details</h2>
              {[
                { k: "name",     label: "Full name *",     type: "text",  placeholder: "Priya Nair" },
                { k: "company",  label: "Company / brand", type: "text",  placeholder: "e.g. Fresh Catch Exports" },
                { k: "email",    label: "Email *",         type: "email", placeholder: "you@company.com" },
                { k: "phone",    label: "Phone / WhatsApp", type: "tel",  placeholder: "+91 ..." },
                { k: "country",  label: "Country *",       type: "text",  placeholder: "India / UAE / US ..." },
              ].map((f) => (
                <label key={f.k} className="block">
                  <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{f.label}</span>
                  <input value={form[f.k] || ""} onChange={set(f.k)} type={f.type} placeholder={f.placeholder}
                    data-testid={`rfq-${f.k}`}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500 outline-none" />
                </label>
              ))}
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Product category *</span>
                <select value={form.product_category || ""} onChange={set("product_category")} data-testid="rfq-product_category"
                  className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500 outline-none bg-white">
                  <option value="">Select…</option>
                  {PRODUCT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="rfq-step-packaging">
              <h2 className="font-heading font-bold text-xl">Packaging format</h2>
              <p className="text-sm text-slate-500">Pick the closest format. If unsure, choose <strong>Help me choose</strong> — our AI assistant or sales team will guide you.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {PACKAGING_FORMATS.map((f) => (
                  <label key={f} className={`block border rounded-lg px-3 py-2.5 cursor-pointer text-sm ${form.packaging_format === f ? "border-emerald-500 bg-emerald-50 font-semibold text-emerald-900" : "border-slate-200 hover:border-emerald-300"}`}>
                    <input type="radio" name="packaging_format" value={f} checked={form.packaging_format === f} onChange={set("packaging_format")} className="mr-2" data-testid={`rfq-fmt-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                    {f}
                  </label>
                ))}
              </div>
              <label className="block pt-2">
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Pre-made pouch or roll stock?</span>
                <select value={form.pouch_or_roll || ""} onChange={set("pouch_or_roll")} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500 outline-none bg-white" data-testid="rfq-pouch_or_roll">
                  <option value="">Select…</option>
                  <option>Pre-made pouch</option>
                  <option>Printed laminated roll (FFS)</option>
                  <option>Both</option>
                  <option>Not sure yet</option>
                </select>
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="rfq-step-specs">
              <h2 className="font-heading font-bold text-xl">Specifications</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  ["pack_dimensions", "Pack dimensions (e.g. 180 × 240 mm)"],
                  ["fill_weight",     "Fill weight / volume (e.g. 500g)"],
                  ["order_quantity",  "Estimated order quantity (per SKU)"],
                  ["number_of_skus",  "Number of SKUs / designs"],
                  ["target_delivery_date", "Target delivery date"],
                  ["target_markets",  "Target markets (India / Export)"],
                ].map(([k, label]) => (
                  <label key={k} className="block">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</span>
                    <input value={form[k] || ""} onChange={set(k)} data-testid={`rfq-${k}`}
                      className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500 outline-none" />
                  </label>
                ))}
              </div>
              <label className="block pt-2">
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Barrier / shelf-life requirement (if known)</span>
                <input value={form.shelf_life || ""} onChange={set("shelf_life")} data-testid="rfq-shelf_life"
                  className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500 outline-none"
                  placeholder="e.g. 12 months at ambient, or export frozen" />
              </label>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="rfq-step-artwork">
              <h2 className="font-heading font-bold text-xl">Artwork & notes</h2>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Artwork status</span>
                <select value={form.artwork_status || ""} onChange={set("artwork_status")} data-testid="rfq-artwork_status"
                  className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500 outline-none bg-white">
                  <option value="">Select…</option>
                  <option>Final print-ready artwork</option>
                  <option>Draft artwork — needs review</option>
                  <option>No artwork — need design support</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Upload artwork / reference image (optional, max 15MB)</span>
                <div className="mt-2 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                  <UploadCloud size={28} className="mx-auto text-slate-300" />
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.ai,.psd,.zip" onChange={(e) => uploadFile(e.target.files?.[0])} className="hidden" id="rfq-file-input" data-testid="rfq-file-input" />
                  <label htmlFor="rfq-file-input" className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 cursor-pointer">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
                    {uploading ? "Uploading…" : "Choose file"}
                  </label>
                </div>
                {uploads.length > 0 && (
                  <ul className="mt-3 space-y-1" data-testid="rfq-upload-list">
                    {uploads.map((u) => (
                      <li key={u.upload_id} className="text-xs text-slate-600 flex items-center gap-2">
                        <Paperclip size={12} className="text-emerald-600" />
                        {u.filename} · {(u.size / 1024).toFixed(1)} KB
                      </li>
                    ))}
                  </ul>
                )}
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Additional notes</span>
                <textarea value={form.notes || ""} onChange={set("notes")} rows={4} data-testid="rfq-notes"
                  className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500 outline-none" />
              </label>
              <label className="flex items-start gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={!!form.consent} onChange={(e) => setForm((s) => ({ ...s, consent: e.target.checked }))} data-testid="rfq-consent" className="mt-0.5" />
                I agree to Crescent Flexi Pack contacting me about this request and understand my data will be handled per the privacy policy.
              </label>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <button
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-30"
              data-testid="rfq-back"
            ><ArrowLeft size={14} /> Back</button>
            {step < STEPS.length - 1 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={!canNext()}
                data-testid="rfq-next"
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2"
              >Continue <ArrowRight size={14} /></button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting || !form.consent}
                data-testid="rfq-submit"
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <>Submit RFQ <ArrowRight size={14} /></>}
              </button>
            )}
          </div>
        </div>

        <p className="mt-6 text-xs text-slate-400 text-center">
          Your uploads are stored securely and never exposed publicly. Our team confirms every spec in writing before production.
        </p>
      </div>
    </CrescentLayout>
  );
}
